import { useState } from 'react';
import { ScoreResult, PerTransactionResult, DecisionLabel } from '../types/index.js';

interface Props {
  results: ScoreResult;
  onPlayAgain: () => void;
  onViewLeaderboard: () => void;
}

function decisionClass(d: DecisionLabel) {
  return `decision-${d.toLowerCase()}`;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(amount);
}

function TransactionRow({ item }: { item: PerTransactionResult }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`result-row ${item.agreedWithAi ? 'correct' : 'incorrect'}`}>
      <button className="result-row-summary" onClick={() => setExpanded((e) => !e)}>
        <span className="result-num">{item.transactionIndex + 1}</span>
        <span className="result-merchant">{item.transaction.merchant}</span>
        <span className="result-amount">
          {new Intl.NumberFormat('en-AU', { style: 'currency', currency: item.transaction.currency }).format(item.transaction.amount)}
        </span>
        <span className={`result-badge ${decisionClass(item.userDecision)}`}>{item.userDecision}</span>
        <span className={`result-badge ai-badge ${decisionClass(item.aiDecision)}`}>AI: {item.aiDecision}</span>
        <span className={`result-agree ${item.agreedWithAi ? 'agree' : 'disagree'}`}>
          {item.agreedWithAi ? 'Agreed' : 'Disagreed'}
        </span>
        <span className="expand-icon">{expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && (
        <div className="result-row-detail">
          <div className="risk-bar-container">
            <span>AI Risk Score</span>
            <div className="risk-bar">
              <div
                className="risk-fill"
                style={{ width: `${item.riskScore}%`, background: item.riskScore >= 66 ? '#ef4444' : item.riskScore >= 26 ? '#f59e0b' : '#22c55e' }}
              />
            </div>
            <span>{item.riskScore}%</span>
          </div>
          <ul className="result-reasons">
            {item.reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export function ResultsDashboard({ results, onPlayAgain, onViewLeaderboard }: Props) {
  const agreementPct = Math.round((results.userScore / 10) * 100);

  const headline =
    results.userScore === 10 ? 'Perfect alignment with the AI' :
    results.userScore >= 8 ? 'Strong agreement with the AI' :
    results.userScore >= 6 ? 'Reasonable alignment with the AI' :
    'Your instincts differed from the AI';

  return (
    <div className="results-dashboard">
      <h1 className="results-title">Case Closed</h1>

      <div className="agreement-card">
        <div className="agreement-score">{results.userScore}<span className="score-denom">/10</span></div>
        <div className="agreement-pct">{agreementPct}% agreement with AI</div>
        <div className="agreement-headline">{headline}</div>
      </div>

      <div className="fraud-stats">
        <div className="fraud-stat">
          <div className="fraud-stat-label">AI flagged as fraud</div>
          <div className="fraud-stat-value">{formatCurrency(results.aiFraudFlagged)}</div>
        </div>
        <div className="fraud-stat">
          <div className="fraud-stat-label">You caught</div>
          <div className="fraud-stat-value positive">{formatCurrency(results.userFraudPrevented)}</div>
        </div>
        <div className="fraud-stat">
          <div className="fraud-stat-label">You missed</div>
          <div className="fraud-stat-value negative">{formatCurrency(results.userFraudMissed)}</div>
        </div>
      </div>

      <div className="results-breakdown">
        <h2>Transaction Breakdown</h2>
        {results.perTransaction.map((item) => (
          <TransactionRow key={item.transactionIndex} item={item} />
        ))}
      </div>

      <div className="results-actions">
        <button className="btn-secondary" onClick={onViewLeaderboard}>View Leaderboard</button>
        <button className="btn-primary" onClick={onPlayAgain}>Play Again</button>
      </div>
    </div>
  );
}
